import Big from "big.js";
import { bigMath } from "../misc";
import {
	OrderInputLike,
	OrderItemInputLike,
	OrderItemOptionInputLike,
} from "../types";
import { $deepPopulateItemMetadata, $isOrderUnpaid } from "./_internals";
import { calculateOrder } from "./calculate-order";

export const applyTaxToOrder = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	order,
	tax,
}: {
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>;
	tax: number;
}) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	const subtotal = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	orderItems = orderItems.map((orderItem) => {
		const grossSales = bigMath.sub(
			orderItem.subtotal,
			orderItem.subtotal_addons.offer.amount,
			orderItem.subtotal_addons.discount.amount,
		);
		const addedVatScRate = bigMath.add(
			orderItem.original_price_addons.vat.percentage,
			orderItem.original_price_addons.service_charge.percentage,
		);
		const taxAmount = new Big(
			new Big(
				new Big(grossSales).sub(
					new Big(grossSales).div(new Big(1).add(addedVatScRate)),
				),
			).mul(tax),
		)
			.div(addedVatScRate || 1)
			.round(2)
			.toNumber();

		const subtotal_addons = {
			...orderItem.subtotal_addons,
			tax: {
				percentage: tax,
				amount: taxAmount,
			},
		};

		return { ...orderItem, subtotal_addons };
	});

	// options only stats
	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const grossSales = bigMath.sub(
				orderItemOption.subtotal,
				orderItemOption.subtotal_addons.offer.amount,
				orderItemOption.subtotal_addons.discount.amount,
			);
			const addedVatScRate = bigMath.add(
				orderItemOption.original_price_addons.vat.percentage,
				orderItemOption.original_price_addons.service_charge.percentage,
			);
			const taxAmount = new Big(
				new Big(
					new Big(grossSales).sub(
						new Big(grossSales).div(new Big(1).add(addedVatScRate)),
					),
				).mul(tax),
			)
				.div(addedVatScRate || 1)
				.round(2)
				.toNumber();

			return {
				...orderItemOption,
				subtotal_addons: {
					...orderItemOption.subtotal_addons,
					tax: {
						percentage: tax,
						amount: taxAmount,
					},
				},
			};
		}),
	}));

	const tax_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.tax?.amount ?? 0),
		0,
	);

	return calculateOrder({
		...order,
		items: orderItems,
		subtotal,
		tax_amount,
	}).$order;
};

export const applyTaxToOrderGroup = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	orders,
	tax,
	skipPaidCheck = false,
}: {
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[];
	tax: number;
	skipPaidCheck?: boolean;
}) => {
	return orders
		.filter((o) => (skipPaidCheck ? true : $isOrderUnpaid(o)))
		.map((order) => applyTaxToOrder({ order, tax }));
};
